/**
 * The validation rules for a client's company record, in one place.
 *
 * They used to live inline in AddClientModal, so the edit surface
 * (ClientInformationSection) enforced a different, weaker set: a CVR could be
 * created as 8 digits and then edited to "abc". Both surfaces now import from
 * here, which is the only way the two stay in agreement.
 */

/**
 * TEMPORARY — CVR registry lookup is disabled.
 *
 * cvrapi.dk withdrew its free tier (Aug 2026); every lookup now returns
 * QUOTA_EXCEEDED, which surfaced as "Company not found in CVR registry".
 * Awaiting system-to-system access to the official CVR registry at
 * Erhvervsstyrelsen (distribution.virk.dk).
 *
 * While disabled: an 8-digit CVR is accepted on format alone, the company is
 * NOT verified to exist, and name/address are not auto-filled.
 *
 * To re-enable once the Virk integration is live: set this to true.
 *
 * NB: apps/public CountryOrganizationStep.tsx still holds its own copy of this
 * constant — a third surface that has to be flipped by hand at the same time.
 */
export const CVR_LOOKUP_ENABLED: boolean = false;

/** The shape of a country's registration requirement, as the API describes it. */
export interface RegistrationRule {
	code: string;
	label: string;
	isRequired?: boolean | null;
}

/**
 * Format rules for a CVR number. Returns an error message, or null when the
 * value is acceptable.
 *
 * An empty value is NOT an error here — whether a blank is allowed is a
 * separate question, answered by validateRegistrations().
 */
export function validateCvrFormat(value: string): string | null {
	if (!value) return null;
	if (!/^\d*$/.test(value)) return 'CVR must contain only digits';
	if (value.length > 8) return 'CVR must be exactly 8 digits';
	if (value.length < 8) return 'CVR must be 8 digits';
	return null;
}

/**
 * Errors for one country's registration set, keyed by registration code so each
 * Field can render its own message.
 *
 * Format only — see the note inside on why a missing required value is not an
 * error. `isRequired` is still read by callers to render the asterisk.
 */
export function validateRegistrations(
	values: Record<string, string>,
	countryRegistrations: readonly RegistrationRule[]
): Record<string, string> {
	const errors: Record<string, string> = {};

	for (const reg of countryRegistrations) {
		const value = (values[reg.code] ?? '').trim();

		/* A blank is not an error. AddClientModal has never enforced isRequired
		   either — it renders the asterisk and lets the value through — so
		   clients exist with no CVR at all. Blocking on presence here would make
		   every one of those permanently unsavable, including for an unrelated
		   edit like fixing a typo in the name. The asterisk carries the hint;
		   only a value that is present and malformed is an error. */
		if (!value) continue;

		if (reg.code === 'CVR') {
			const cvrError = validateCvrFormat(value);
			if (cvrError) errors[reg.code] = cvrError;
		}
	}

	return errors;
}

/** The company fields both the create and the edit surface collect. */
export interface ClientDetailsValues {
	name: string;
	countryCode: string;
}

/**
 * Errors for the company details, keyed by field name.
 *
 * Country is required on create (the API types it `String!` there) and the
 * registration fields are derived from it, so the edit surface holds it to the
 * same rule rather than letting a client drift into having none.
 */
export function validateClientDetails(values: ClientDetailsValues): Record<string, string> {
	const errors: Record<string, string> = {};
	if (!values.name.trim()) errors.name = 'Client name is required';
	if (!values.countryCode) errors.countryCode = 'Country is required';
	return errors;
}
